import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTableDataSource } from '@angular/material/table';
import { SetSelectedCheckSheetAssignments } from '@completion/actions';
import { CheckSheetAssignmentProjection } from '@completion/models';
import { getAvailableCheckSheetAssignments, getSelectedSupplierCheckSheetAssignments } from '@completion/selectors';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-line-tag-scope',
  templateUrl: './line-tag-scope.component.html',
  styleUrls: ['./line-tag-scope.component.scss'],
  standalone: false,
})
export class LineTagScopeComponent implements OnInit, OnDestroy {
  assignmentDataSource = new MatTableDataSource<CheckSheetAssignmentProjection>();
  lineAssignmentColumns: string[] = ['select', 'tag', 'checksheet', 'status', 'assignedCount'];
  lineAssignmentsGroupedByCommissioningPackage: { commissioningPackageNumber: string; assignments: CheckSheetAssignmentProjection[] }[] =
    [];
  selectedAssignments: CheckSheetAssignmentProjection[] = [];
  availableCheckSheetAssignments: CheckSheetAssignmentProjection[] = [];
  filterText = '';
  hideUnassigned = false;
  private destroy$ = new Subject<void>();

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.store
      .select(getAvailableCheckSheetAssignments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.availableCheckSheetAssignments = [...data];
          this.applyFilter();
        }
      });

    this.store
      .select(getSelectedSupplierCheckSheetAssignments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selected) => {
        this.selectedAssignments = selected ? [...selected] : [];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectAssignment(assignment: CheckSheetAssignmentProjection, event: MatCheckboxChange): void {
    if (event.checked) {
      if (!this.selectedAssignments.some((selected) => selected.id === assignment.id)) {
        this.selectedAssignments = [...this.selectedAssignments, assignment];
      }
    } else {
      this.selectedAssignments = this.selectedAssignments.filter((selected) => selected.id !== assignment.id);
    }
    this.store.dispatch(new SetSelectedCheckSheetAssignments(this.selectedAssignments));
  }

  isAssignmentSelected(assignment: CheckSheetAssignmentProjection): boolean {
    return this.selectedAssignments.some((selected) => selected.id === assignment.id);
  }

  clearFilter(): void {
    this.filterText = '';
    this.applyFilter();
  }

  onFilterKeyup(): void {
    this.applyFilter();
  }

  toggleHideUnassigned(): void {
    this.hideUnassigned = !this.hideUnassigned;
    this.applyFilter();
  }

  getAssignedCountPillClass(a: CheckSheetAssignmentProjection): string {
    const c: any = (a as any).assignedCount;
    if (c == null) return 'none';
    if (c === 1) return 'single';
    if (c > 1) return 'multiple';
    return 'none';
  }

  getAssignedCountLabel(a: CheckSheetAssignmentProjection): string {
    const c: any = (a as any).assignedCount;
    if (c == null) return 'No assignments';
    if (c === 1) return '1 user assigned';
    return `${c} users assigned`;
  }

  private applyFilter(): void {
    const f = this.filterText.trim().toLowerCase();
    let base = [...this.availableCheckSheetAssignments];
    if (this.hideUnassigned) {
      base = base.filter((a) => Number((a as any).assignedCount ?? 0) === 0);
    }

    if (!f) {
      this.assignmentDataSource.data = base;
      this.rebuildLineGroups();
      return;
    }

    this.assignmentDataSource.data = base.filter((a) => {
      const fields: string[] = [
        a.commissioningPackageNumber,
        a.checkSheetNumber,
        a.tagNumber,
        a.companyNumber,
        a.phaseNumber,
        a.id != null ? String(a.id) : '',
      ]
        .filter((v) => v != null && v !== '')
        .map((v) => v.toString().toLowerCase());
      return fields.some((val) => val.includes(f));
    });
    this.rebuildLineGroups();
  }

  private rebuildLineGroups(): void {
    const groups = new Map<string, CheckSheetAssignmentProjection[]>();
    for (const assignment of this.assignmentDataSource.data) {
      const key = assignment.commissioningPackageNumber || 'No commissioning package';
      const existing = groups.get(key) || [];
      existing.push(assignment);
      groups.set(key, existing);
    }
    this.lineAssignmentsGroupedByCommissioningPackage = Array.from(groups.entries())
      .map(([commissioningPackageNumber, assignments]) => ({ commissioningPackageNumber, assignments }))
      .sort((a, b) => a.commissioningPackageNumber.localeCompare(b.commissioningPackageNumber));
  }
}
